import * as audio from './kernel/audio.js';
import{ createPanner, positionListener, orientListener } from './kernel/audio/panner.js';
import * as spectrum from './kernel/audio/spectrum.js';
import * as waveform from './kernel/audio/waveform.js';
import { degToRad } from './kernel/utils.js';

export {
    init,
    load,
    start,
}

const images = [
    { name: 'background', url: new URL('../images/carte-topo.png', import.meta.url) },
    { name: 'mic', url: new URL('../images/mic.png', import.meta.url) },
];

const sounds = [
    { name: 'test-headphone', url: new URL('../sounds/TestLR.m4a', import.meta.url) },
    { name: 'signal', url: new URL('../sounds/SoundEscape.m4a', import.meta.url) },
    { name: 'commentaire1', url: new URL('../sounds/DescriptionAlo.m4a', import.meta.url) },
    { name: 'commentaire2', url: new URL('../sounds/DescriptionBern.m4a', import.meta.url) },
    { name: 'commentaire3', url: new URL('../sounds/DescriptionIsa.m4a', import.meta.url) },
    { name: 'commentaire4', url: new URL('../sounds/DescriptionLeon.m4a', import.meta.url) },
    { name: 'commentaire5', url: new URL('../sounds/DescriptionLouve.m4a', import.meta.url) },
    { name: 'commentaire6', url: new URL('../sounds/DescriptionMic.m4a', import.meta.url) },
    { name: 'commentaire7', url: new URL('../sounds/DescriptionNico.m4a', import.meta.url) },
    { name: 'commentaire8', url: new URL('../sounds/DescriptionVJ.m4a', import.meta.url) },
    { name: 'commentaire9', url: new URL('../sounds/DescriptionYves.m4a', import.meta.url) },
];

const commentaries = [
    { name: 'commentaire1', x: 510, y: 275 },
    { name: 'commentaire2', x: 420, y: 370 },
    { name: 'commentaire3', x: 400, y: 735 },
    { name: 'commentaire4', x: 635, y: 240 },
    { name: 'commentaire5', x: 760, y: 685 },
    { name: 'commentaire6', x: 1345, y: 205 },
    { name: 'commentaire7', x: 1445, y: 435 },
    { name: 'commentaire8', x: 1480, y: 260 },
    { name: 'commentaire9', x: 1245, y: 810 },
    { name: 'commentaire1', x: 1909, y: 93 },
    { name: 'commentaire2', x: 1888, y: 847 },
    { name: 'commentaire3', x: 1088, y: 356 },
    { name: 'commentaire4', x: 96, y: 894 },
    { name: 'commentaire5', x: 1807, y: 108 },
    { name: 'commentaire6', x: 596, y: 886 },
    { name: 'commentaire7', x: 102, y: 433 },
    { name: 'commentaire8', x: 746, y: 940 },
    { name: 'commentaire9', x: 1013, y: 63 },
]

const screen = document.querySelector('.screen');
const micEl = document.querySelector('.mic');
const joystickEl = document.querySelector('.joystick');

const worldSize = {
    width: 3840,
    height: 2160,
};

const joystickSize = 60;
const commentarySize = 12;

const mic = {
    position: { x: 611, y: 412 },
    rotation: -11,
    damping: 0.08,
    speed: 0,
    turnSpeed: 0,
    acceleration: 0.1,
}

const positionOffset = { x: 0, y: 0 };

const coordinatesMultiplier = 2; // Coordinates are in FHD, world is in 4K

const keys = {};

const touch = {
    active: false,
    startX: 0,
    startY: 0,
    xAxis: 0,
    yAxis: 0,
};

function init() {
    // Register window resize event
    window.addEventListener('resize', () => {
        updateMicElPos();
        updateCommentariesPos();
    });

    // Register keyboard events
    window.addEventListener('keydown', (e) => {
        keys[e.key] = true;
    });
    window.addEventListener('keyup', (e) => {
        keys[e.key] = false;
    });

    // Register touch events
    window.addEventListener('touchstart', (e) => {
        touch.active = true;
        touch.startX = e.touches[0].clientX;
        touch.startY = e.touches[0].clientY;
        joystickEl.style.translate = `${touch.startX - joystickSize / 2}px ${touch.startY - joystickSize / 2}px`;
        joystickEl.style.opacity = 0.5;
    });
    window.addEventListener('touchmove', (e) => {
        const halfJoy = joystickSize / 2;

        // Get touch move position
        let moveX = e.touches[0].clientX;
        let moveY = e.touches[0].clientY;

        // Calculate joystick delta
        let deltaX = moveX - touch.startX;
        let deltaY = moveY - touch.startY;

        // Calculate joystick distance
        const distance = Math.sqrt(deltaX ** 2 + deltaY ** 2);

        // Constrain joystick to its size
        if (distance > halfJoy) {
            const angle = Math.atan2(deltaY, deltaX);
            deltaX = Math.cos(angle) * halfJoy;
            deltaY = Math.sin(angle) * halfJoy;
            moveX = touch.startX + deltaX;
            moveY = touch.startY + deltaY;
        }

        touch.xAxis = deltaX / halfJoy;
        touch.yAxis = deltaY / halfJoy;

        // Update joystick position
        joystickEl.style.translate = `${moveX - halfJoy}px ${moveY - halfJoy}px`;
    });
    window.addEventListener('touchend', (e) => {
        touch.active = false;
        joystickEl.style.opacity = 0;
    });
}

function load() {
    return Promise.all([
        loadSounds(),
        loadImages(),
    ]);
}

function start() {
    if (DEVMODE) console.log('Scene started');

    updateMicElPos();

    // Play signal
    const signal = audio.buffers.play('signal', {
        loop: true,
    });

    // Setup spectrum audio analyser
    const spectrumAudioAnalyser = audio.context.createAnalyser();
    const fftBins = 1024;
    spectrumAudioAnalyser.fftSize = fftBins * 2;
    spectrumAudioAnalyser.smoothingTimeConstant = 0.0;
    const spectrumData = new Float32Array(fftBins);

    signal.gainNode.connect(spectrumAudioAnalyser);

    // Setup spectrum module
    const spectrumCanvas = document.querySelector('#spectrum');
    spectrumCanvas.width = 250;
    spectrumCanvas.height = 74;
    spectrum.setup({
        cellWidth: spectrumCanvas.width,
        cellHeight: spectrumCanvas.height,
        devicePixelRatio: 1,
    });

    // Get spectrum canvas context
    const spectrumCtx = spectrumCanvas.getContext('2d');

    // Setup waveform module
    const waveformCanvas = document.querySelector('#waveform');
    waveformCanvas.width = 250;
    waveformCanvas.height = 74;
    waveform.setup({
        cellWidth: waveformCanvas.width,
        cellHeight: waveformCanvas.height,
        devicePixelRatio: 1,
    });

    // Get waveform canvas context
    const waveformCtx = waveformCanvas.getContext('2d');

    // Setup waveform audio analyser
    const waveformAudioAnalyser = audio.context.createAnalyser();
    spectrumAudioAnalyser.fftSize = fftBins * 2;
    spectrumAudioAnalyser.smoothingTimeConstant = 0.0;
    const waveformData = new Float32Array(fftBins);

    commentaries.forEach(commentary => {
        // Create panner
        const panner = createPanner();
        panner.positionPanner(commentary.x, 0, commentary.y);

        // Play commentaries
        const sound = audio.buffers.play(commentary.name, {
            loop: true,
            destination: panner,
        });
        panner.connect(waveformAudioAnalyser);

        // Add commentary to the screen
        const el = document.createElement('div');
        el.classList.add('commentary');
        el.classList.add(commentary.name);
        screen.appendChild(el);
        commentary.el = el;
    });

    updateCommentariesPos();

    loop();

    function loop() {
        // Update mic speed and rotation from keyboard
        if (keys.ArrowUp) mic.speed += mic.acceleration;
        if (keys.ArrowDown) mic.speed -= mic.acceleration;
        if (keys.ArrowLeft) mic.turnSpeed -= mic.acceleration;
        if (keys.ArrowRight) mic.turnSpeed += mic.acceleration;

        // Update mic speed and rotation from touch
        if (touch.active) {
            mic.speed += mic.acceleration * -touch.yAxis;
            mic.turnSpeed += mic.acceleration * touch.xAxis;
        }

        // Update mic
        mic.speed *= 1 - mic.damping;
        mic.turnSpeed *= 1 - mic.damping;
        mic.position.x += Math.cos(degToRad(mic.rotation - 90)) * mic.speed;
        mic.position.y += Math.sin(degToRad(mic.rotation - 90)) * mic.speed;
        mic.rotation += mic.turnSpeed;

        // Constrain position to topographic map boundaries
        const margin = 20;
        mic.position.x = Math.max(margin, Math.min(mic.position.x, (worldSize.width / coordinatesMultiplier) - margin));
        mic.position.y = Math.max(margin, Math.min(mic.position.y, (worldSize.height / coordinatesMultiplier) - margin));

        // Offset background position, mic position and commentaries position
        // when mic is near the edge of the screen
        const screenPos = worldToScreenPosition(mic.position);
        if (screenPos.x < screen.clientWidth / 4) {
            positionOffset.x = screen.clientWidth / 4 - screenPos.x;
        } else if (screenPos.x > screen.clientWidth * 3 / 4) {
            positionOffset.x = screen.clientWidth * 3 / 4 - screenPos.x;
        } else {
            positionOffset.x = 0;
        }
        if (screenPos.y < screen.clientHeight / 4) {
            positionOffset.y = screen.clientHeight / 4 - screenPos.y;
        } else if (screenPos.y > screen.clientHeight * 3 / 4) {
            positionOffset.y = screen.clientHeight * 3 / 4 - screenPos.y;
        } else {
            positionOffset.y = 0;
        }
        screen.style.backgroundPosition = `calc(50% + ${positionOffset.x}px) calc(50% + ${positionOffset.y}px)`;
        
        updateMicElPos();
        updateCommentariesPos();

        // 3D HRTF panning
        positionListener(mic.position.x, 0, mic.position.y);
        const deltaAngleX = Math.abs(mic.rotation + 90) % 360;
        const deviationX = (deltaAngleX < 180) ? deltaAngleX : 360 - deltaAngleX;
        const deltaAngleZ = Math.abs(mic.rotation) % 360;
        const deviationZ = (deltaAngleZ < 180) ? deltaAngleZ : 360 - deltaAngleZ;
        orientListener((deviationX / 180) * 2 - 1, 0, (deviationZ / 180) * 2 - 1);

        // Draw spectrum
        spectrumAudioAnalyser.getFloatFrequencyData(spectrumData);
        spectrumCtx.clearRect(0, 0, spectrumCanvas.width, spectrumCanvas.height);
        spectrumCtx.drawImage(spectrum.draw(spectrumData), 0, 0);
        spectrumCtx.fillStyle = '#ff8800';
        spectrumCtx.font = '14px Source Code Pro';
        spectrumCtx.fillText(`Signal spectrum`, 5, 16);

        // Draw waveform
        waveformAudioAnalyser.getFloatTimeDomainData(waveformData);
        waveformCtx.clearRect(0, 0, waveformCanvas.width, waveformCanvas.height);
        waveformCtx.drawImage(waveform.draw(waveformData), 0, 0);
        waveformCtx.fillStyle = '#ff8800';
        waveformCtx.font = '14px Source Code Pro';
        waveformCtx.fillText(`Microphone waveform`, 5, 16);

        requestAnimationFrame(loop);
    }
}

function loadSounds() {
    return Promise.all([
        ...sounds.map(sound => {
            return audio.buffers.load(sound.name, sound.url);
        }),
    ]).then(() => {
        if (DEVMODE) console.log('All sounds loaded');
    });
}

function loadImages() {
    return Promise.all([
        ...images.map(image => {
            return new Promise((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                    if (DEVMODE) console.log(`Image ${image.name} loaded`);
                    resolve();
                };
                img.onerror = () => {
                    reject(`Error loading image ${image.name}`);
                };
                img.src = image.url;
            });
        }),
    ]).then(() => {
        if (DEVMODE) console.log('All images loaded');
    });
}

function updateMicElPos() {
    const screenPos = worldToScreenPosition(mic.position);
    micEl.style.translate = `${positionOffset.x + screenPos.x}px ${positionOffset.y + screenPos.y}px`;
    micEl.style.rotate = `${mic.rotation}deg`;
    micEl.style.scale = `${screenPos.scale}`;
}

function updateCommentariesPos() {
    commentaries.forEach(commentary => {
        const screenPos = worldToScreenPosition(commentary);
        commentary.el.style.translate = `${positionOffset.x + screenPos.x - commentarySize / 2}px ${positionOffset.y + screenPos.y - commentarySize / 2}px`;
        commentary.el.style.scale = `${screenPos.scale}`;
    });
}

function worldToScreenPosition(worldPos) {
    // Screen background-size is set to cover
    const screenRatio = screen.clientWidth / screen.clientHeight;
    const worldRatio = worldSize.width / worldSize.height;
    let scale = 1;
    if (screenRatio > worldRatio) {
        // Screen less tall than the world
        // Topography is clipped on the top and bottom
        scale = screen.clientWidth / worldSize.width;
        return {
            x: coordinatesMultiplier * worldPos.x * scale,
            y: coordinatesMultiplier * worldPos.y * scale - (worldSize.height * scale - screen.clientHeight) / 2,
            scale: coordinatesMultiplier * scale,
        };
    } else {
        // Screen is less wide than the world
        // Topography is clipped on the sides
        scale = screen.clientHeight / worldSize.height;
        return {
            x: coordinatesMultiplier * worldPos.x * scale - (worldSize.width * scale - screen.clientWidth) / 2,
            y: coordinatesMultiplier * worldPos.y * scale,
            scale: coordinatesMultiplier * scale,
        };
    }
}
